import { html, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import style from './purchases-and-sales-table.scss?inline';
import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './purchases-and-sales-table.constants.js';
import type { PurchasesAndSalesTableContentObject } from './purchases-and-sales-table.model.js';
import { isEmptyArray, isNullish } from '../../common/utils/nullish';
import {
  formatToNDecimalPlaces,
  formatNumberToShowSign,
} from '../../common/utils/formatNumber';

/**
 * @tag pop-purchases-and-sales-table-component
 * @attribute { PurchasesAndSalesTableContentObject } [data-component-object] PurchasesAndSalesTable content object
 */
@customElement(Constants.TAG_NAME)
export class PopPurchasesAndSalesTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * PurchasesAndSalesTable content object
   */
  componentObject?: PurchasesAndSalesTableContentObject;

  private renderTableHead() {
    return html`
      <tr>
        <th id="name-column" scope="col">
          <beacon-table-header variant="normal">
            ${Constants.NAME_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th
          id="prev-quarter-portfolio-weight-column"
          scope="col"
          class="right-align"
        >
          <beacon-table-header variant="normal">
            ${Constants.PREV_QUARTER_PORTFOLIO_WEIGHT_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="portfolio-weight-column" scope="col" class="right-align">
          <beacon-table-header variant="normal">
            ${Constants.PORTFOLIO_WEIGHT_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="change-column" scope="col" class="right-align">
          <beacon-table-header variant="normal">
            ${Constants.CHANGE_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="sector-column" scope="col">
          <beacon-table-header variant="normal">
            ${Constants.SECTOR_COLUMN_HEADER}
          </beacon-table-header>
        </th>
      </tr>
    `;
  }

  private renderTableBody() {
    const itemList = this.componentObject?.itemList;

    return html`
      ${itemList?.map((item) => {
        return html`
          <tr>
            <th>
              <beacon-table-stub>
                <span
                  class="b-typography-sans-serif--body--100 b-font-weight-bold"
                >
                  ${item.name || Constants.EMPTY_CELL_VALUE}
                </span>
              </beacon-table-stub>
            </th>
            <td>
              <beacon-table-record variant="numeral">
                ${formatToNDecimalPlaces(
                  item.previousQuarterPercentTotalNetAssets,
                  2,
                )}
              </beacon-table-record>
            </td>
            <td>
              <beacon-table-record variant="numeral">
                ${formatToNDecimalPlaces(item.percentTotalNetAssets, 2)}
              </beacon-table-record>
            </td>
            <td>
              <beacon-table-record variant="numeral">
                ${formatNumberToShowSign(item.previousQuarterChange)}
              </beacon-table-record>
            </td>
            <td>
              <beacon-table-record variant="text">
                ${item.sectorName || Constants.EMPTY_CELL_VALUE}
              </beacon-table-record>
            </td>
          </tr>
        `;
      })}
    `;
  }

  render() {
    if (
      isEmptyArray(this.componentObject?.itemList) ||
      isNullish(this.componentObject?.itemList)
    ) {
      return html``;
    }

    return this.renderComponent(html`
      <beacon-table variant="fixed">
        <table tabindex="0">
          <thead>
            ${this.renderTableHead()}
          </thead>
          <tbody>
            ${this.renderTableBody()}
          </tbody>
        </table>
      </beacon-table>
    `);
  }
}
